import { useEffect, useRef, useState } from "react";
import { Head } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import { organization_files_path } from "@/routes";

const ONLYOFFICE_SCRIPT_ID = "onlyoffice-docs-api";

function loadOnlyofficeApi(apiUrl) {
  if (window.DocsAPI?.DocEditor) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(ONLYOFFICE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("ONLYOFFICE API kunne ikke indlæses.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = ONLYOFFICE_SCRIPT_ID;
    script.src = apiUrl;
    script.async = true;
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", () => reject(new Error("ONLYOFFICE API kunne ikke indlæses.")), { once: true });
    document.head.appendChild(script);
  });
}

function onlyofficeErrorMessage(errorData) {
  if (!errorData) return "ONLYOFFICE kunne ikke åbne filen.";
  if (typeof errorData !== "object") return `ONLYOFFICE kunne ikke åbne filen (fejl ${errorData}).`;

  const description = errorData.errorDescription || errorData.message;
  const code = errorData.errorCode || errorData.code;
  if (description && code) return `${description} (fejl ${code}).`;
  if (description) return description;
  if (code) return `ONLYOFFICE kunne ikke åbne filen (fejl ${code}).`;

  return "ONLYOFFICE kunne ikke åbne filen.";
}

function OrganizationFilesEdit({ organization_file: organizationFile, onlyoffice }) {
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const documentLabel = onlyoffice.config.documentType === "cell" ? "regnearket" : "dokumentet";

  useEffect(() => {
    let disposed = false;

    loadOnlyofficeApi(onlyoffice.api_url)
      .then(() => {
        if (disposed) return;

        editorRef.current = new window.DocsAPI.DocEditor("onlyoffice-editor", {
          ...onlyoffice.config,
          events: {
            onAppReady: () => setLoading(false),
            onError: (event) => {
              setLoading(false);
              setError(onlyofficeErrorMessage(event?.data));
            }
          }
        });
      })
      .catch((loadError) => {
        if (disposed) return;

        setLoading(false);
        setError(loadError.message);
      });

    return () => {
      disposed = true;
      editorRef.current?.destroyEditor?.();
      editorRef.current = null;
    };
  }, [onlyoffice.api_url, onlyoffice.config]);

  return (
    <>
      <Head title={`Rediger ${organizationFile.filename}`} />

      <section className="workspace-content organization-files-editor-page">
        <div className="organization-files-editor-page__surface">
          <div id="onlyoffice-editor" />

          {loading ? (
            <div className="organization-files-editor-page__status" role="status">
              <span className="spinner-border text-primary" aria-hidden="true" />
              <span>Åbner {documentLabel}…</span>
            </div>
          ) : null}

          {error ? (
            <div className="organization-files-editor-page__status organization-files-editor-page__status--error" role="alert">
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

OrganizationFilesEdit.layout = (page) => (
  <AuthLayout
    header={{
      breadcrumbs: [
        { label: "Filer", href: organization_files_path() },
        { label: page.props.organization_file.filename }
      ],
      title: page.props.onlyoffice.config.documentType === "cell" ? "Rediger regneark" : "Rediger dokument",
      actions: [
        {
          href: organization_files_path(),
          label: "Tilbage til filer",
          variant: "outline-secondary",
          icon: "bi bi-arrow-left"
        }
      ]
    }}
  >
    {page}
  </AuthLayout>
);

export default OrganizationFilesEdit;
